import { useState, useCallback } from "react";
import axios from "axios";
import { Trash2, AlertTriangle, Loader2, Search, ShieldAlert, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const API = window.location.origin + "/api";

// Il backend supporta qualsiasi status (empty string = "tutti"). L'UI
// espone gli stati canonici GHL così l'admin può fare pulizie mirate:
// - "" (Tutti): comprende anche appuntamenti attivi → CAUTELA
// - "cancelled": cancellati (uso più comune, safe)
// - altri: usi eccezionali, richiedono attenzione
const STATUS_OPTIONS = [
  { value: "cancelled", label: "Cancellati" },
  { value: "noshow", label: "Non presentati (no-show)" },
  { value: "invalid", label: "Non validi" },
  { value: "showed", label: "Presentati (showed)" },
  { value: "confirmed", label: "Confermati (attivi) ⚠️" },
  { value: "", label: "Tutti gli stati ⚠️" },
];

// Stati "distruttivi" — richiedono doppio-check nell'UI perché eliminano
// appuntamenti attivi/passati validi, non solo cancellati/no-show.
const DESTRUCTIVE_STATUSES = new Set(["", "confirmed", "showed"]);

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export default function BulkDeleteAppointments() {
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(todayIso());
  // Lista appuntamenti selezionati per cancellazione massiva: di default
  // filtriamo "cancelled" (unica opzione visibile nel dropdown ora).
  const [statusFilter, setStatusFilter] = useState("cancelled");
  const [searching, setSearching] = useState(false);
  const [searchData, setSearchData] = useState(null);
  const [hidden, setHidden] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!startDate || !endDate) {
      toast.error("Seleziona date di inizio e fine");
      return;
    }
    setSearching(true);
    setSearchData(null);
    try {
      const { data } = await axios.post(
        `${API}/appointments/bulk-delete-doctors`,
        { start_date: startDate, end_date: endDate, status: statusFilter },
        { withCredentials: true }
      );
      setSearchData(data);
      if ((data.total || 0) === 0) {
        toast.info("Nessun appuntamento in 'Calendario Generale' nel range selezionato");
      } else {
        toast.success(`Trovati ${data.total} app.ti distribuiti su ${data.doctors.length} dottori`);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setHidden(true);
      } else {
        toast.error(err.response?.data?.detail || "Errore durante la ricerca");
      }
    } finally {
      setSearching(false);
    }
  }, [startDate, endDate, statusFilter]);

  const handleOpenConfirm = (doctor) => {
    setSelectedDoctor(doctor);
    setPassword("");
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!password || !selectedDoctor) return;
    setDeleting(true);
    try {
      const { data } = await axios.post(
        `${API}/appointments/bulk-delete`,
        {
          assigned_user_id: selectedDoctor.user_id,
          start_date: startDate,
          end_date: endDate,
          status: statusFilter,
          password,
        },
        { withCredentials: true, timeout: 600000 }
      );
      toast.success(data.message);
      if (data.failed > 0) toast.warning(`${data.failed} eliminazioni fallite`);
      setConfirmOpen(false);
      setSelectedDoctor(null);
      setPassword("");
      handleSearch();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  if (hidden) return null;

  return (
    <div
      className="bg-white rounded-2xl shadow-sm border border-slate-100 p-5 space-y-4"
      data-testid="bulk-delete-tool"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <ShieldAlert className="w-4 h-4 text-red-600" />
        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
          Eliminazione massiva &mdash; Azione Irreversibile
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-wider text-red-700 bg-red-100 border border-red-200 px-2 py-0.5 rounded-full">
          Admin
        </span>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Cerca gli appuntamenti del <strong>&laquo;Calendario Generale&raquo;</strong> in un intervallo
        di date e scegli il <strong>dottore assegnato</strong> di cui eliminare definitivamente
        gli appuntamenti da GHL. Verrà chiesta una conferma testuale prima dell&apos;esecuzione.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
            Data inizio
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => { setStartDate(e.target.value); setSearchData(null); }}
            data-testid="bulk-delete-start"
            className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-[#1a1fa8]"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
            Data fine
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => { setEndDate(e.target.value); setSearchData(null); }}
            data-testid="bulk-delete-end"
            className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-[#1a1fa8]"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">
            Stato
          </label>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setSearchData(null); }}
            data-testid="bulk-delete-status"
            className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-[#1a1fa8]"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <Button
            type="button"
            onClick={handleSearch}
            disabled={searching}
            variant="outline"
            className="w-full rounded-xl h-10"
            data-testid="bulk-delete-search-btn"
          >
            {searching ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
            Cerca dottori
          </Button>
        </div>
      </div>

      {DESTRUCTIVE_STATUSES.has(statusFilter) && (
        <div
          className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2"
          data-testid="destructive-status-warning"
        >
          <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-800 leading-relaxed">
            <strong>Attenzione:</strong> hai selezionato uno stato che include
            appuntamenti <strong>attivi o storicamente validi</strong>. Assicurati
            che sia davvero necessario cancellarli, sono <strong>irrecuperabili</strong>.
          </div>
        </div>
      )}

      {searchData && searchData.doctors && searchData.doctors.length > 0 && (
        <div className="border-t border-slate-100 pt-4 space-y-2" data-testid="bulk-delete-doctors-list">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <h4 className="text-xs font-bold uppercase tracking-wide text-slate-500 flex items-center gap-2">
              <Stethoscope className="w-3.5 h-3.5" />
              Dottori con appuntamenti dal {startDate} al {endDate}
            </h4>
            <span className="text-xs text-slate-500">
              Totale: <strong className="text-slate-800">{searchData.total}</strong> app.ti
            </span>
          </div>
          <div className="space-y-1.5">
            {searchData.doctors.map((d) => (
              <div
                key={d.user_id || "unassigned"}
                className="flex items-center justify-between gap-3 px-3 py-2 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg"
                data-testid={`doctor-row-${d.user_id || "unassigned"}`}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800 truncate">
                    {d.doctor_name}
                  </span>
                  <span className="text-xs font-bold text-slate-700 bg-white border border-slate-200 px-2 py-0.5 rounded-full">
                    {d.count} app.ti
                  </span>
                  {Object.entries(d.by_status || {}).length > 0 && (
                    <span className="text-[11px] text-slate-400">
                      ({Object.entries(d.by_status).map(([k, v]) => `${k}: ${v}`).join(", ")})
                    </span>
                  )}
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => handleOpenConfirm(d)}
                  disabled={deleting}
                  className="rounded-lg shrink-0"
                  data-testid={`delete-doctor-${d.user_id || "unassigned"}`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Elimina {d.count}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="bulk-delete-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Conferma eliminazione definitiva
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-2 text-slate-700">
              <span className="block">
                Stai per eliminare <strong>{selectedDoctor?.count || 0}</strong> appuntamenti
                {statusFilter && (
                  <> con stato <strong>{STATUS_OPTIONS.find(o => o.value === statusFilter)?.label}</strong></>
                )}
                {" "}del Dott. <strong>{selectedDoctor?.doctor_name}</strong> dal calendario{" "}
                <strong>&laquo;Calendario Generale&raquo;</strong> nel periodo dal{" "}
                <strong>{startDate}</strong> al <strong>{endDate}</strong>.
              </span>
              <span className="block text-red-600 font-semibold">
                Questa azione è IRREVERSIBILE. Gli appuntamenti spariranno da GHL definitivamente.
              </span>
              <span className="block">
                Per confermare, inserisci la <strong>tua password</strong> di accesso:
              </span>
            </DialogDescription>
          </DialogHeader>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && password && !deleting) {
                e.preventDefault();
                handleConfirmDelete();
              }
            }}
            placeholder="La tua password"
            data-testid="bulk-delete-password-input"
            autoFocus
            autoComplete="current-password"
            className="w-full h-10 text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-red-500"
          />
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={deleting}
              data-testid="bulk-delete-cancel-btn"
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={!password || deleting}
              data-testid="bulk-delete-confirm-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
